import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  fetchNotifications,
  fetchNotificationCount,
  dismissNotificationApi,
  fetchNotificationRules,
  updateNotificationRuleApi,
  deleteNotificationRuleApi,
  createNotificationRuleApi,
  seedNotificationRules,
  triggerEvaluation,
  clearAllNotifications,
  type Notification,
  type NotificationRule,
} from '../services/notificationService';
import { useAppSession } from '../contexts/AppSessionContext';

// ─── Notifications ───────────────────────────────────────────────────

export function useNotifications(filters?: {
  status?: string;
  category?: string;
  formula_code?: string;
  limit?: number;
}) {
  const { status, user } = useAppSession();
  const accountId = user?.accountId;
  return useQuery<Notification[]>(
    ['notifications', accountId, filters],
    () => fetchNotifications(accountId, filters),
    {
      enabled: status === 'authenticated' && !!accountId,
      staleTime: 60_000,
      refetchInterval: 60_000,
      retry: 1,
    },
  );
}

export function useActiveNotifications() {
  return useNotifications({ status: 'active' });
}

export function useNotificationCount() {
  const { status, user } = useAppSession();
  const accountId = user?.accountId;
  return useQuery<number>(
    ['notification-count', accountId],
    () => fetchNotificationCount(accountId),
    {
      enabled: status === 'authenticated' && !!accountId,
      staleTime: 30_000,
      refetchInterval: 60_000,
      retry: 1,
    },
  );
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  const { user } = useAppSession();
  const accountId = user?.accountId;
  return useMutation(
    (notificationId: number) => dismissNotificationApi(notificationId, accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notification-count']);
      },
    },
  );
}

// ─── Notification Rules ──────────────────────────────────────────────

export function useNotificationRules(filters?: {
  enabled?: boolean;
  category?: string;
  formula_code?: string;
}) {
  const { status, user } = useAppSession();
  const accountId = user?.accountId;
  return useQuery<NotificationRule[]>(
    ['notification-rules', accountId, filters],
    () => fetchNotificationRules(accountId, filters),
    {
      enabled: status === 'authenticated' && !!accountId,
      staleTime: 5 * 60_000,
      retry: 1,
    },
  );
}

export function useCreateNotificationRule() {
  const queryClient = useQueryClient();
  const { user } = useAppSession();
  const accountId = user?.accountId;
  return useMutation(
    (rule: Parameters<typeof createNotificationRuleApi>[1]) => createNotificationRuleApi(accountId, rule),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-rules']);
      },
    },
  );
}

export function useUpdateNotificationRule() {
  const queryClient = useQueryClient();
  const { user } = useAppSession();
  const accountId = user?.accountId;
  return useMutation(
    ({ ruleId, updates }: { ruleId: number; updates: Parameters<typeof updateNotificationRuleApi>[1] }) =>
      updateNotificationRuleApi(ruleId, updates, accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-rules']);
      },
    },
  );
}

export function useDeleteNotificationRule() {
  const queryClient = useQueryClient();
  const { user } = useAppSession();
  const accountId = user?.accountId;
  return useMutation(
    (ruleId: number) => deleteNotificationRuleApi(ruleId, accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-rules']);
      },
    },
  );
}

export function useSeedNotificationRules() {
  const queryClient = useQueryClient();
  const { user } = useAppSession();
  const accountId = user?.accountId;
  return useMutation(
    (opts?: { reset?: boolean }) => seedNotificationRules(accountId, opts?.reset ?? false),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-rules']);
      },
    },
  );
}

// ─── Evaluation ──────────────────────────────────────────────────────

export function useClearAllNotifications() {
  const queryClient = useQueryClient();
  const { user } = useAppSession();
  const accountId = user?.accountId;
  return useMutation(
    () => clearAllNotifications(accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notification-count']);
      },
    },
  );
}

export function useTriggerEvaluation() {
  const queryClient = useQueryClient();
  const { user } = useAppSession();
  const accountId = user?.accountId;
  return useMutation(
    () => triggerEvaluation(accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notification-count']);
      },
    },
  );
}
